import { useQuery } from '@apollo/client';
import {
  liquidGlassCircleShadowSx,
  liquidGlassPressTransition,
  liquidGlassSurfaceFillSx,
  LIQUID_GLASS_CIRCLE_PRESS_SCALE,
  LIQUID_GLASS_PRESS_ORIGIN,
  PremiumStarBadge,
  useLiquidGlassPress,
} from '@focuslee/shared-mini-app';
import { Backdrop, Box, Grow } from '@mui/material';
import { hapticFeedback } from '@tma.js/sdk';
import { motion } from 'framer-motion';
import { useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetProfileQuery, GetProfileQueryVariables, UserRole } from '../../client/generated/graphql';
import { getProfileQuery } from '../../client/queries/profile';
import { useVoice } from '../../contexts/VoiceContext';
import { AchievementProgressFloatingBlock } from '../achievements/AchievementProgressFloatingBlock';
import {
  FLOATING_TABBAR_ACTION_SIZE,
  FLOATING_TABBAR_BAR_GAP,
  FLOATING_TABBAR_HORIZONTAL_INSET,
  FLOATING_TABBAR_SATELLITE_GAP,
  FLOATING_TABBAR_SATELLITE_SIZE,
  floatingTabbarBlockHeightPx,
} from '../../helpers/floatingTabbarLayout';
import {
  achievementCreatePath,
  categoryCreatePath,
  premiumPath,
  questCreatePath,
  questTemplatesPath,
} from '../../navigation/paths';
import { CommonIcon } from '../common/CommonIcon';
import { LiquidGlassCircleButton } from '../common/LiquidGlassCircleButton';
import { VoiceExpandMenuAchievement } from './VoiceExpandMenuAchievement';
import { VoiceExpandMenuCategory } from './VoiceExpandMenuCategory';
import { VoiceExpandMenuQuest } from './VoiceExpandMenuQuest';

interface VoiceTabbarButtonProps {
  currentTabId: string;
}

const menuBottomOffset = `calc(${floatingTabbarBlockHeightPx}px + ${FLOATING_TABBAR_BAR_GAP}px + var(--tg-viewport-content-safe-area-inset-bottom, 0px) + var(--tg-viewport-safe-area-inset-bottom, 0px))`;

export const VoiceTabbarButton: FC<VoiceTabbarButtonProps> = ({ currentTabId }) => {
  const navigate = useNavigate();
  const { startVoice, isVoiceButtonDisabled, isRecording } = useVoice();
  const { data } = useQuery<GetProfileQuery, GetProfileQueryVariables>(getProfileQuery);
  const [open, setOpen] = useState(false);

  const isPremium = data?.user.role === UserRole.Premium || data?.user.role === UserRole.Admin;
  const isDisabled = isVoiceButtonDisabled;
  const showExpandButton = currentTabId !== 'journal' && currentTabId !== 'profile';
  const showSatellite = showExpandButton && !isRecording;

  const handleMainClick = () => {
    if (isDisabled) return;
    hapticFeedback.impactOccurred.ifAvailable('light');
    if (!isPremium) {
      navigate(premiumPath);
      return;
    }
    startVoice();
  };

  const handleMenuToggle = () => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    setOpen((prev) => !prev);
  };

  const handleQuestAction = (action: 'new' | 'template') => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    if (action === 'new') {
      navigate(questCreatePath.replace('/:categoryId?', ''));
    } else {
      navigate(questTemplatesPath);
    }
    setOpen(false);
  };

  const handleAchievementAction = () => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    navigate(achievementCreatePath.replace('/:categoryId?', ''));
    setOpen(false);
  };

  const handleCategoryAction = () => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    navigate(categoryCreatePath);
    setOpen(false);
  };

  const {
    pressed: mainPressed,
    onPointerDown: mainPointerDown,
    onPointerMove: mainPointerMove,
    onPointerUp: mainPointerUp,
    onPointerCancel: mainPointerCancel,
    onClick: mainClick,
  } = useLiquidGlassPress({ disabled: isDisabled, onActivate: handleMainClick });

  return (
    <>
      {currentTabId === 'achievements' && <AchievementProgressFloatingBlock />}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1399,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          pointerEvents: 'auto',
        }}
        open={open}
        onClick={() => setOpen(false)}
      />
      {open && (
        <Box
          sx={{
            position: 'fixed',
            left: `${FLOATING_TABBAR_HORIZONTAL_INSET}px`,
            right: `${FLOATING_TABBAR_HORIZONTAL_INSET}px`,
            bottom: menuBottomOffset,
            zIndex: 1401,
            pointerEvents: 'auto',
          }}
        >
          <Grow in={open} style={{ transformOrigin: 'center bottom' }} {...(open ? { timeout: 300 } : {})}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {currentTabId === 'quests' && (
                <VoiceExpandMenuQuest
                  onNewQuest={() => handleQuestAction('new')}
                  onFromTemplate={() => handleQuestAction('template')}
                />
              )}
              {currentTabId === 'achievements' && (
                <VoiceExpandMenuAchievement onNewAchievement={handleAchievementAction} />
              )}
              {currentTabId === 'categories' && (
                <VoiceExpandMenuCategory onNewCategory={handleCategoryAction} />
              )}
            </Box>
          </Grow>
        </Box>
      )}
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: FLOATING_TABBAR_ACTION_SIZE,
        }}
      >
        {showSatellite && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: `calc(100% + ${FLOATING_TABBAR_SATELLITE_GAP}px)`,
              transform: 'translateX(-50%)',
              width: FLOATING_TABBAR_SATELLITE_SIZE,
            }}
          >
            <LiquidGlassCircleButton
              icon={open ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
              ariaLabel={open ? 'Close quick actions' : 'Quick actions'}
              onClick={handleMenuToggle}
              size={FLOATING_TABBAR_SATELLITE_SIZE}
              iconSize={18}
            />
          </Box>
        )}
        <Box
          component={motion.div}
          initial={false}
          animate={{ scale: mainPressed ? LIQUID_GLASS_CIRCLE_PRESS_SCALE : 1 }}
          transition={liquidGlassPressTransition}
          sx={{
            width: FLOATING_TABBAR_ACTION_SIZE,
            transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: FLOATING_TABBAR_ACTION_SIZE,
              height: FLOATING_TABBAR_ACTION_SIZE,
              borderRadius: '50%',
              overflow: 'visible',
              ...liquidGlassCircleShadowSx,
            }}
          >
            {!isPremium && <PremiumStarBadge />}
            <Box
              component="button"
              type="button"
              aria-label={isRecording ? 'Stop' : 'Use voice'}
              onClick={mainClick}
              onPointerDown={mainPointerDown}
              onPointerMove={mainPointerMove}
              onPointerUp={mainPointerUp}
              onPointerCancel={mainPointerCancel}
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: 'inherit',
                border: 'none',
                cursor: isDisabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isRecording ? 'white' : 'var(--tg-theme-text-color)',
                bgcolor: isRecording ? 'var(--tg-theme-destructive-text-color)' : undefined,
                pointerEvents: 'auto',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                transition: 'background-color 0.25s ease, color 0.2s ease',
                ...(isRecording ? {} : liquidGlassSurfaceFillSx),
                '&:focus-visible': {
                  outline: '2px solid var(--tg-theme-link-color)',
                  outlineOffset: 2,
                },
              }}
            >
              <CommonIcon icon={isRecording ? 'stop' : 'graphic_eq'} type="filled" size={28} color="currentColor" />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
